import { useTranslations } from 'next-intl';
import { useState, useTransition } from 'react';

import { cartService } from '@/fo/api/magento';
import BonusIcon from '@/fo/icons/base/icons/bonus.svg';
import BonusIconActive from '@/fo/icons/base/icons/bonus_white.svg';
import { UiKitInputPromo } from '@/fo/ui/kit/input/promo';
import { UiKitLoaderBase } from '@/fo/ui/kit/loader/base';
import { UiKitPanelBase } from '@/fo/ui/kit/panel/base';
import { utilEventCartModal } from '@/fo/util/event-cart-modal';
import { utilPluralize } from '@/fo/util/pluralize';

import { styles } from './Promo.styles';
export interface OrderSummaryPromo {
  value?: string;
  message?: string;
  error?: boolean;
  success?: boolean;
  loading?: boolean;
}

export interface PromoProps {
  hasSpendBonuses?: boolean;
  canSpendBonuses?: boolean;
  bonuses?: number;
  subtotalPrice?: number;
  promo?: OrderSummaryPromo | null;
  certificate?: OrderSummaryPromo | null;
  onPromoSubmit?: (value: string) => void;
  onPromoClear?: () => void;
  onTabChange?: (index: number) => void;
  onGiftPromoChange?: (show: boolean) => void;
  isAuthorized?: boolean;
}

export function Promo({
  hasSpendBonuses,
  canSpendBonuses,
  bonuses = 0,
  subtotalPrice = 0,
  promo,
  onPromoSubmit,
  onPromoClear,
  onTabChange,
  isAuthorized
}: PromoProps) {
  const slots = styles();
  const t = useTranslations();
  const [isPending, startTransition] = useTransition();
  const [activeTab, setActiveTab] = useState(hasSpendBonuses ? 1 : 0);
  const [promoValue, setPromoValue] = useState('');
  const minSpend = 0;
  const pluralizeBalance = utilPluralize({
    one: t('pluralize.balance.one'),
    few: t('pluralize.balance.few'),
    many: t('pluralize.balance.many')
  });
  const handleTabChange = (index: number) => {
    setActiveTab(index);

    startTransition(async () => {
      if (index === 1 && bonuses > 0) {
        if (promo?.value && promo.value.trim() !== '') {
          onPromoClear?.();
        }

        await cartService.applyBonusPointsToCart(bonuses);
      }

      if (index === 0) {
        await cartService.removeBonusPointsFromCart();
      }
      utilEventCartModal.mutate();
      onTabChange?.(index);
    });
  };

  const handlePromoSubmit = () => {
    onPromoSubmit?.(promoValue);
  };

  const promoBlock = promo ? (
    <UiKitInputPromo
      value={promo.value}
      helperText={promo.message}
      placeholder={t('Enter certificate')}
      error={promo.error}
      success={promo.success}
      loading={promo.loading}
      onSubmit={handlePromoSubmit}
      onClear={onPromoClear}
      onChange={setPromoValue}
    />
  ) : null;

  const promosBlock = (
    <div className={slots.promos()}>
      <div className={slots.promo()}>{promoBlock}</div>
    </div>
  );
  const panelItems =
    canSpendBonuses && bonuses > 0 && isAuthorized
      ? [
          {
            header: (
              <>
                <span className={slots.tabTitle()}>{t('Apply promocode')}</span>
              </>
            ),
            content: promosBlock
          },
          {
            header: (
              <span className={slots.tabTitle()}>
                <BonusIcon className={slots.bonusIcon()} />
                <BonusIconActive className={slots.bonusIcon()} />
                {`${t('Spend')} ${bonuses} ${pluralizeBalance(bonuses)}`}
              </span>
            ),
            content: <></>,
            disabled: !canSpendBonuses || subtotalPrice <= minSpend
          }
        ]
      : [];

  return canSpendBonuses && bonuses > 0 && isAuthorized ? (
    <div className="relative">
      <UiKitPanelBase
        view="tab"
        items={panelItems}
        activeIndex={activeTab}
        onTabChange={handleTabChange}
        tabClassName={slots.tab()}
        tabsClassName={slots.tabs()}
        tabsWrapClassName={slots.tabsWrap()}
        tabActiveClassName={slots.activeTab()}
      />
      {isPending && <UiKitLoaderBase />}
    </div>
  ) : (
    promosBlock
  );
}
